import { useCallback, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@/components/icons';

// ─── Period ──────────────────────────────────────────────────────────────────
export type RangeUnit = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type PeriodUnit = 'all' | RangeUnit | 'custom';
export interface PeriodRange { from: string; to: string; unit: RangeUnit | 'custom'; label: string }

/** Home dashboard set. */
export const DASHBOARD_UNITS: { key: PeriodUnit; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }, { key: 'quarter', label: 'Quarter' }, { key: 'year', label: 'Year' },
];
/** Report pages: all time, a from–to date range, a month, a FY quarter or a year. */
export const REPORT_UNITS: { key: PeriodUnit; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'custom', label: 'Date' }, { key: 'month', label: 'Month' }, { key: 'quarter', label: 'Quarter' }, { key: 'year', label: 'Year' },
];

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n: number) => String(n).padStart(2, '0');
export const ymd = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const parseYmd = (v: string) => { const [y, m, d] = v.split('-').map(Number); return new Date(y, m - 1, d); };
const dayText = (d: Date, year = true) => `${pad2(d.getDate())} ${MON[d.getMonth()]}${year ? ` ${d.getFullYear()}` : ''}`;

/** Financial-year quarter containing the given quarter start (Q1 = Apr–Jun … Q4 = Jan–Mar): "Oct–Dec 2026 · Q3 FY 26-27". */
const quarterLabel = (start: Date) => {
  const m = start.getMonth();
  const fyStart = m >= 3 ? start.getFullYear() : start.getFullYear() - 1;
  const q = Math.floor(((m + 9) % 12) / 3) + 1;
  return `${MON[m]}–${MON[m + 2]} ${start.getFullYear()} · Q${q} FY ${String(fyStart).slice(2)}-${String(fyStart + 1).slice(2)}`;
};

/** The from–to (inclusive) and display label of the period containing `anchor`. Weeks run Monday–Sunday. */
export function periodRange(unit: RangeUnit, anchor: string): PeriodRange {
  const a = parseYmd(anchor);
  let start: Date;
  let end: Date;
  let label: string;
  if (unit === 'day') { start = a; end = a; label = dayText(a); }
  else if (unit === 'week') {
    start = new Date(a.getFullYear(), a.getMonth(), a.getDate() - ((a.getDay() + 6) % 7));
    end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    label = `${dayText(start, start.getFullYear() !== end.getFullYear())} – ${dayText(end)}`;
  } else if (unit === 'month') {
    start = new Date(a.getFullYear(), a.getMonth(), 1);
    end = new Date(a.getFullYear(), a.getMonth() + 1, 0);
    label = `${MON[a.getMonth()]} ${a.getFullYear()}`;
  } else if (unit === 'quarter') {
    start = new Date(a.getFullYear(), a.getMonth() - (a.getMonth() % 3), 1);
    end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
    label = quarterLabel(start);
  } else {
    start = new Date(a.getFullYear(), 0, 1);
    end = new Date(a.getFullYear(), 11, 31);
    label = String(a.getFullYear());
  }
  return { from: ymd(start), to: ymd(end), unit, label };
}

/** Move the anchor one period back (-1) or forward (+1). */
export function shiftAnchor(unit: RangeUnit, anchor: string, dir: number) {
  const a = parseYmd(anchor);
  if (unit === 'day') return ymd(new Date(a.getFullYear(), a.getMonth(), a.getDate() + dir));
  if (unit === 'week') return ymd(new Date(a.getFullYear(), a.getMonth(), a.getDate() + 7 * dir));
  if (unit === 'month') return ymd(new Date(a.getFullYear(), a.getMonth() + dir, 1));
  if (unit === 'quarter') return ymd(new Date(a.getFullYear(), a.getMonth() - (a.getMonth() % 3) + 3 * dir, 1));
  return ymd(new Date(a.getFullYear() + dir, 0, 1));
}

// !w-auto: a FilterBar stretches every input/select to full width; the picker's own fields keep their size.
const PICK_INPUT = '!w-auto h-7 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 text-xs font-semibold text-[var(--text-primary)] outline-none focus:border-accent';

export function PeriodPicker({ unit, anchor, onUnit, onAnchor, units = DASHBOARD_UNITS, custom, className = 'justify-end', stretch = true }: {
  unit: PeriodUnit; anchor: string; onUnit: (u: PeriodUnit) => void; onAnchor: (a: string) => void;
  units?: { key: PeriodUnit; label: string }[];
  /** From–To inputs shown when the unit is 'custom'. */
  custom?: { from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void };
  /** Row alignment — right-aligned in a page header, left inside a filter bar. */
  className?: string;
  /** Phones: the unit buttons and the date stepper each span the full width (default). */
  stretch?: boolean;
}) {
  const today = ymd(new Date());
  const range = unit === 'all' || unit === 'custom' ? null : periodRange(unit, anchor);
  const nextDisabled = !range || periodRange(range.unit as RangeUnit, shiftAnchor(range.unit as RangeUnit, anchor, 1)).from > today;
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => thisYear - i);
  // The current and previous 7 quarters, newest first.
  const quarters = Array.from({ length: 8 }, (_, i) => periodRange('quarter', shiftAnchor('quarter', today, -i)));
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      <div className={`${stretch ? 'flex w-full sm:inline-flex sm:w-auto' : 'inline-flex'} rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-0.5`} role="group" aria-label="Period">
        {units.map((u) => (
          <button
            key={u.key} type="button" onClick={() => onUnit(u.key)} aria-pressed={unit === u.key}
            className={`${stretch ? 'flex-1 sm:flex-none' : ''} rounded-md px-2.5 py-1 text-[11px] font-bold transition ${unit === u.key ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-secondary)] hover:text-accent'}`}
          >
            {u.label}
          </button>
        ))}
      </div>
      {unit === 'custom' && custom && (
        <div className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-0.5 pl-2">
          <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">From</span>
          <input type="date" className={PICK_INPUT} value={custom.from} max={custom.to || today} aria-label="From date" onChange={(e) => custom.onFrom(e.target.value)} />
          <span className="pl-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">To</span>
          <input type="date" className={PICK_INPUT} value={custom.to} min={custom.from || undefined} max={today} aria-label="To date" onChange={(e) => custom.onTo(e.target.value)} />
        </div>
      )}
      {range && (
        <div className={`${stretch ? 'flex w-full justify-between sm:inline-flex sm:w-auto' : 'inline-flex'} items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-0.5`}>
          <button type="button" onClick={() => onAnchor(shiftAnchor(range.unit as RangeUnit, anchor, -1))} title={`Previous ${unit}`} className="rounded-md p-1 text-[var(--text-secondary)] hover:bg-accent/10 hover:text-accent">
            <ChevronLeft size={14} />
          </button>
          {(unit === 'day' || unit === 'week') && (
            <input type="date" className={PICK_INPUT} value={anchor} max={today} aria-label={unit === 'day' ? 'Day' : 'Any day in the week'} onChange={(e) => e.target.value && onAnchor(e.target.value)} />
          )}
          {unit === 'month' && (
            <input type="month" className={PICK_INPUT} value={anchor.slice(0, 7)} max={today.slice(0, 7)} aria-label="Month" onChange={(e) => e.target.value && onAnchor(`${e.target.value}-01`)} />
          )}
          {unit === 'quarter' && (
            <select className={PICK_INPUT} value={range.from} aria-label="Quarter" onChange={(e) => onAnchor(e.target.value)}>
              {!quarters.some((q) => q.from === range.from) && <option value={range.from}>{range.label}</option>}
              {quarters.map((q) => <option key={q.from} value={q.from}>{q.label}</option>)}
            </select>
          )}
          {unit === 'year' && (
            <select className={PICK_INPUT} value={anchor.slice(0, 4)} aria-label="Year" onChange={(e) => onAnchor(`${e.target.value}-01-01`)}>
              {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          )}
          {unit === 'week' && <span className="px-1 text-[11px] font-semibold text-[var(--text-secondary)]">{range.label}</span>}
          <button type="button" onClick={() => onAnchor(shiftAnchor(range.unit as RangeUnit, anchor, 1))} disabled={nextDisabled} title={`Next ${unit}`} className="rounded-md p-1 text-[var(--text-secondary)] hover:bg-accent/10 hover:text-accent disabled:opacity-30 disabled:hover:bg-transparent">
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/** Local calendar date (YYYY-MM-DD) of a timestamp or date string; null when missing or unparsable. */
export function localYmd(v: string | null | undefined): string | null {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : ymd(d);
}

/**
 * Period state for a report page. `range` is null for "All" (and for an empty custom range);
 * `inRange` tests a timestamp against it, so pages filter client-side rows the same way the
 * server filters by `range.from` / `range.to`.
 */
export function usePeriod(initial: PeriodUnit = 'all') {
  const [unit, setUnitRaw] = useState<PeriodUnit>(initial);
  const [anchor, setAnchor] = useState(() => ymd(new Date()));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = useMemo<PeriodRange | null>(() => {
    if (unit === 'all') return null;
    // An open end gets a far bound the server's date column can still hold.
    if (unit === 'custom') {
      if (!from && !to) return null;
      const label = from && from === to ? dayText(parseYmd(from)) : `${from ? dayText(parseYmd(from)) : '…'} – ${to ? dayText(parseYmd(to)) : '…'}`;
      return { from: from || '1900-01-01', to: to || '2999-12-31', unit: 'custom', label };
    }
    return periodRange(unit, anchor);
  }, [unit, anchor, from, to]);
  const inRange = useCallback((v: string | null | undefined) => {
    if (!range) return true;
    const d = localYmd(v);
    return !!d && d >= range.from && d <= range.to;
  }, [range]);
  const setUnit = (u: PeriodUnit) => {
    setUnitRaw(u); setAnchor(ymd(new Date()));
    // A date range opens on today, so picking it shows today straight away.
    if (u === 'custom' && !from && !to) { const t = ymd(new Date()); setFrom(t); setTo(t); }
  };
  return {
    unit, anchor, range, inRange,
    /** Spread onto <PeriodPicker>. */
    picker: { unit, anchor, onUnit: setUnit, onAnchor: setAnchor, custom: { from, to, onFrom: setFrom, onTo: setTo } },
  };
}
